// src/services/referralLandingService.js
import supabase from '../lib/supabase';

const STORAGE_KEY = 'sohocard.referral_code';
const TTL_HOURS = 72;

/* ============================================================
   Public referral landing service
   ============================================================ */

/**
 * Verify a referral code exists and return the referrer's public info.
 * Uses the v_public_referrer view so anon callers can query it safely.
 */
export async function lookupReferralCode(code) {
    if (!code) return null;
    const clean = String(code).trim().toUpperCase();

    const { data, error } = await supabase
        .from('v_public_referrer')
        .select('id, name, avatar_url, referral_code, created_at')
        .eq('referral_code', clean)
        .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    // Mask the name for privacy: "Ali Rezaei" → "Ali R."
    const full = (data.name || '').trim();
    const parts = full.split(/\s+/);
    const displayName = parts.length > 1
        ? `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`
        : (parts[0] || 'Someone');

    return {
        id: data.id,
        code: data.referral_code,
        displayName,
        avatar_url: data.avatar_url,
        memberSince: data.created_at,
    };
}

/**
 * Store the referral code in localStorage so signup can pick it up later.
 * Expires after TTL_HOURS so stale links don't persist forever.
 */
export function rememberReferralCode(code) {
    if (!code) return;
    try {
        const payload = {
            code: String(code).trim().toUpperCase(),
            storedAt: Date.now(),
            expiresAt: Date.now() + TTL_HOURS * 60 * 60 * 1000,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        /* no-op */
    }
}

export function getRememberedReferralCode() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.code) return null;
        if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed.code;
    } catch {
        return null;
    }
}

export function clearRememberedReferralCode() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* no-op */
    }
}

/* ============================================================
   ⭐ attachReferral
   ------------------------------------------------------------
   Called from AuthContext.signUp() and Register.jsx right after
   Supabase creates the new user. Reads the referral code the
   visitor saved earlier and inserts a pending row into
   sohocard.referrals, then updates profiles.referred_by.
   ============================================================ */
export async function attachReferral(newUserId, code) {
    if (!newUserId || !code) return null;

    const clean = String(code).trim().toUpperCase();

    // 1. Look up the referrer (uses the public view — safe for anon + auth)
    const referrer = await lookupReferralCode(clean);
    if (!referrer || referrer.id === newUserId) return null;

    // 2. Insert the pending referral
    const { data, error } = await supabase
        .from('referrals')
        .insert({
            referrer_id: referrer.id,
            referee_id: newUserId,
            code: clean,
            status: 'pending',
            reward_amount: 0,
        })
        .select()
        .single();

    if (error) {
        // Ignore unique violation — this referee was already attributed
        if (error.code === '23505') return null;
        throw error;
    }

    // 3. Mirror the referrer onto the profile (best-effort)
    try {
        await supabase
            .from('profiles')
            .update({ referred_by: referrer.id })
            .eq('id', newUserId);
    } catch {
        /* non-fatal */
    }

    return data;
}